import { useState } from 'react';
import { ApiError } from '@/api/client';
import { TermoVigenteOut, postAdesao } from '@/api/alianca';
import { Button } from '@/components/ui/Button';
import { BowArc } from '@/components/ui/BowArc';
import { Eyebrow } from '@/components/ui/Typography';
import { Grain } from '@/components/alianca/AliancaPrimitives';

interface AdesaoModalProps {
  termo: TermoVigenteOut;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Confirmação final de adesão à Aliança.
 *
 * Abre como bottom-sheet (mobile) ou centered modal (sm+) com:
 *  - Cabeçalho dark com BowArc e o convite curto.
 *  - Termo escrolável em fundo cream.
 *  - Checkbox "Li e concordo" + CTA "Aderir".
 *
 * Espelha o "convite" (V4 Captação) — o cliente já viu o seletor de níveis
 * na rota /alianca e clicou em "Aderir à Aliança"; este modal é só o passo
 * legal final (LGPD §5.1).
 */
export function AdesaoModal({ termo, onClose, onSuccess }: AdesaoModalProps) {
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirmar() {
    if (!agree) return;
    setBusy(true);
    setError(null);
    try {
      await postAdesao(termo.version);
      onSuccess();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'erro ao aderir');
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-bow-royal/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl bg-bow-cream shadow-deep sm:mb-8 sm:max-h-[80vh] sm:rounded-3xl"
      >
        {/* Header dark com gradient arco */}
        <div
          className="relative overflow-hidden text-white"
          style={{
            background:
              'linear-gradient(135deg, #001B74 0%, #1d2a8c 55%, #8345BA 130%)'
          }}
        >
          <Grain opacity={0.1} />
          <div
            className="pointer-events-none absolute"
            style={{ top: -60, right: -60, opacity: 0.22 }}
          >
            <BowArc size={200} variant="white" spin />
          </div>

          <div className="relative px-6 pt-6 pb-5">
            <div className="flex items-center gap-2.5">
              <BowArc size={28} variant="white" />
              <Eyebrow color="rgba(255,255,255,0.7)">
                Programa de fidelidade
              </Eyebrow>
            </div>
            <h2
              className="mt-2 text-bow-cream"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 32,
                letterSpacing: '-0.03em',
                lineHeight: 1
              }}
            >
              Aderir à Aliança
            </h2>
            <p className="mt-2 text-sm leading-relaxed opacity-90">
              {termo.summary}
            </p>
          </div>
        </div>

        {/* Termo scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <Eyebrow>Termo de uso · v{termo.version}</Eyebrow>
          <pre className="mt-3 whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-bow-royal/85">
            {termo.body_md}
          </pre>
        </div>

        {/* Footer */}
        <div
          className="border-t border-bow-royal/10 bg-white px-6 pt-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <label className="flex cursor-pointer items-start gap-3 text-sm text-bow-royal/85">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5 h-4 w-4 cursor-pointer accent-bow-royal"
              disabled={busy}
            />
            <span>
              Li e concordo com os termos do programa{' '}
              <strong>versão {termo.version}</strong>.
            </span>
          </label>

          {error && (
            <p className="mt-3 text-center text-sm text-bow-red">{error}</p>
          )}

          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button
              variant="ghost"
              size="md"
              fullWidth
              disabled={busy}
              onClick={onClose}
            >
              Agora não
            </Button>
            <Button
              variant="primary"
              size="md"
              fullWidth
              disabled={!agree || busy}
              onClick={handleConfirmar}
            >
              {busy ? 'Aderindo…' : 'Aderir'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
